import React from 'react'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'error' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  fullWidth?: boolean
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  disabled,
  className = '',
  ...props
}: ButtonProps) {
  // Base classes with soft rounded corners and smooth transitions
  const baseClasses =
    'button-premium hover-lift hover-glow relative inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] overflow-hidden'

  // Variant classes with soft gradients and warm colors
  const variantClasses = {
    // Primary: Soft gradient with glow on hover
    primary:
      'bg-gradient-primary text-white focus:ring-primary-300 focus:ring-offset-[var(--color-bg)] dark:focus:ring-offset-[var(--color-bg)] shadow-md hover:shadow-lg',
    // Secondary: Warm neutral with subtle hover
    secondary:
      'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 focus:ring-neutral-400 border border-neutral-200 hover:border-neutral-300 dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700 dark:hover:bg-neutral-700',
    // Success: Encouraging green gradient
    success:
      'bg-gradient-to-br from-success-500 to-success-600 text-white hover:from-success-400 hover:to-success-500 focus:ring-success-400 shadow-sm hover:shadow-md',
    // Error: Soft coral gradient (not alarming)
    error:
      'bg-gradient-to-br from-error-400 to-error-500 text-white hover:from-error-300 hover:to-error-400 focus:ring-error-400 shadow-sm hover:shadow-md',
    // Outline: Transparent with primary border
    outline:
      'bg-transparent border-2 border-primary-500 text-primary-600 hover:bg-primary-50 focus:ring-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20',
    // Ghost: Minimal, just text with hover background
    ghost:
      'bg-transparent text-primary-600 hover:bg-primary-50 focus:ring-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20',
  }

  // Size classes with comfortable padding for touch targets
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm min-h-[36px]',
    md: 'px-4 py-2.5 text-base min-h-[44px]',
    lg: 'px-6 py-3 text-lg min-h-[52px]',
  }

  const widthClass = fullWidth ? 'w-full' : ''

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  )
}
